import api from '.';
import { ORDER } from '../common/enum/order';
import { AccessControl } from '../pages/MasterData/AccessControl/accessControlSlice';
import { Contractor } from '../pages/MasterData/Contractor/contractorSlice';
import { Discipline } from '../pages/MasterData/Discipline/disciplineSlice';
import { Document } from '../pages/MasterData/Document/documentSlice';
import { User } from '../pages/UserProfile/userProfileSlice';

interface GetProjectDocuments {
  projectId?: number | null;
  purposeId?: number[] | null;
  page: number;
  limit: number;
  sortBy?: string;
  orderBy?: ORDER;
  disciplineId?: number[] | null;
  documentCreatorIds?: number[];
  search?: string | null;
}

interface GetAllDocumentNo {
  projectId: number;
  disciplineIds?: number[];
}

interface ReturnTypeDocumentNo {
  data: string[];
}

export interface ProjectDocument {
  projectId: number | null;
  discipline: Discipline | null;
  document: Document | null;
  no: string;
  name: string;
  consultant: Contractor | null;
  contractor: Contractor | null;
  subContractor: Contractor | null;
  clientDocumentNo: string | null;
  epcDocumentNo: string | null;
  vendorDocumentNo: string | null;
  accessControl: AccessControl | null;
  documentCreator: User | null;
  isAIMS: boolean;
  aimsDocumentNo: string | null;
}
export interface CreateProjectDocument extends ProjectDocument {
  customAccessControlUsers: User[];
}

export interface BulkProjectDocument {
  name: string;
  no: string;
  accessControlId: number;
  documentCreatorId: number;
  disciplineId: number;
  documentId: number;
  isAIMS: boolean;
  projectId: number;
  projectDocumentRevisions: {
    planIssueDate: Date;
    planCompletedDate: Date;
    manHour: number;
    purposeId: number;
    revisionId: number;
    isAutoApproval: boolean;
  }[];
  consultantId?: number;
  contractorId?: number;
  subContractorId?: number;
}

export interface CreateBulkProjectDocument {
  projectDocuments: BulkProjectDocument[];
}

export interface CreateBulkPipingProjectDocument
  extends CreateBulkProjectDocument {
  pipingProjectDetail: {
    projectId: number;
    pipingListIds: number[];
  };
}

export interface UpdateProjectDocument extends ProjectDocument {
  id: number | null;
}

export interface DeleteWithReason {
  id: number;
  reason: string;
}

export async function getProjectDocuments({
  projectId,
  purposeId,
  page,
  limit,
  sortBy,
  orderBy,
  disciplineId,
  documentCreatorIds,
  search
}: GetProjectDocuments) {
  const params = {
    projectId,
    purposeId,
    page,
    limit,
    sortBy,
    orderBy,
    disciplineId,
    documentCreatorIds,
    search
  };
  const { data } = await api.get('/project-documents', { params });
  return data;
}

export async function getProjectDocument(id: number) {
  const { data } = await api.get(`/project-documents/${id}`);
  return data;
}

export async function getAllProjectDocumentNos({
  projectId,
  disciplineIds
}: GetAllDocumentNo): Promise<ReturnTypeDocumentNo> {
  const params = {
    projectId,
    disciplineIds
  };
  const { data } = await api.get(`/project-documents/document-no`, { params });
  return data;
}

export async function createProjectDocument(
  projectDocument: CreateProjectDocument
) {
  const createProjectDocument = {
    no: projectDocument.no,
    consultantId: projectDocument.consultant?.id ?? null,
    contractorId: projectDocument.contractor?.id ?? null,
    subContractorId: projectDocument.subContractor?.id ?? null,
    clientDocumentNo: projectDocument.clientDocumentNo,
    epcDocumentNo: projectDocument.epcDocumentNo,
    vendorDocumentNo: projectDocument.vendorDocumentNo,
    name: projectDocument.name,
    documentCreatorId: projectDocument.documentCreator?.id ?? null,
    documentId: projectDocument.document?.id ?? null,
    disciplineId: projectDocument.discipline?.id ?? null,
    projectId: projectDocument.projectId,
    isAIMS: projectDocument.isAIMS,
    aimsDocumentNo: projectDocument.aimsDocumentNo,
    accessControlId: projectDocument.accessControl?.id ?? null,
    customAccessControlUserIds: projectDocument.customAccessControlUsers.map(
      (customAccessControlUser) => customAccessControlUser.id
    )
  };
  const { data } = await api.post('/project-documents', createProjectDocument);
  return data;
}

export async function bulkCreateProjectDocuments(
  createProjectDocuments: CreateBulkProjectDocument
) {
  const { data } = await api.post(
    '/project-documents/bulk-create',
    createProjectDocuments
  );
  return data;
}

export async function bulkCreatePipingProjectDocuments(
  createBulkPipingProjectDocument: CreateBulkPipingProjectDocument
) {
  const { data } = await api.post(
    '/project-documents/pip-bulk-create',
    createBulkPipingProjectDocument
  );
  return data;
}

export async function updateProjectDocument(
  projectDocument: UpdateProjectDocument
) {
  const updateProjectDocument = {
    id: projectDocument.id,
    no: projectDocument.no,
    consultantId: projectDocument.consultant?.id ?? null,
    contractorId: projectDocument.contractor?.id ?? null,
    subContractorId: projectDocument.subContractor?.id ?? null,
    clientDocumentNo: projectDocument.clientDocumentNo,
    epcDocumentNo: projectDocument.epcDocumentNo,
    vendorDocumentNo: projectDocument.vendorDocumentNo,
    name: projectDocument.name,
    documentCreatorId: projectDocument.documentCreator?.id ?? null,
    documentId: projectDocument.document?.id ?? null,
    disciplineId: projectDocument.discipline?.id ?? null,
    projectId: projectDocument.projectId,
    isAIMS: projectDocument.isAIMS,
    aimsDocumentNo: projectDocument.aimsDocumentNo,
    accessControlId: projectDocument.accessControl?.id ?? null
  };
  const { data } = await api.put(
    `/project-documents/${updateProjectDocument.id}`,
    updateProjectDocument
  );
  return data;
}

export function deleteProjectDocumentWithReason({
  id,
  reason
}: DeleteWithReason): Promise<void> {
  return api.put(`/project-documents/${id}/delete`, { reason });
}

export function deleteProjectDocument(id: number) {
  return api.delete(`/project-documents/${id}`);
}

// Document Master List API - Plug and Play
interface GetDocumentMasterList {
  page?: number;
  limit?: number;
  sortBy?: string;
  orderBy?: ORDER;
  search?: string;
  disciplineIds?: number[];
  aimsDocumentTypeIds?: number[];
}

export interface DocumentMasterListItem {
  id: number;
  no: string;
  name: string;
  documentCreatorId: number;
  isAIMS: boolean;
  aimsDocumentNo: string | null;
  clientDocumentNo: string | null;
  vendorDocumentNo: string | null;
  epcDocumentNo: string | null;
  documentId: number;
  disciplineId: number;
  projectId: number;
  accessControlId: number;
  consultantId: number | null;
  contractorId: number | null;
  subContractorId: number | null;
  createdAt: string;
  updatedAt: string;
  accessControl: AccessControl;
  document: Document;
  discipline: Discipline;
  projectDocumentRevisions: {
    id: number;
    planIssueDate: string;
    planCompletedDate: string;
    manHour: number;
    note: string | null;
    linkName: string | null;
    linkUrl: string | null;
    isAutoApproval: boolean;
    purposeId: number;
    revisionId: number;
    projectDocumentId: number;
    currentProjectDocumentWorkflowId: number;
    createdAt: string;
    updatedAt: string;
    currentProjectDocumentWorkflow: {
      id: number;
      name: string;
      finishedDate: string | null;
      linkName: string | null;
      linkUrl: string | null;
      isAutoApproval: boolean;
      creatorId: number;
      isCreatorViewOnlyExternal: boolean;
      isCreatorViewOnlyFinal: boolean;
      workflowStatusId: number;
      projectDocumentRevisionId: number;
      currentWorkflowStepId: number;
      createdAt: string;
      updatedAt: string;
      workflowStatus: {
        id: number;
        name: string;
        createdAt: string;
        updatedAt: string;
      };
    };
    revision?: {
      id: number;
      name: string;
    };
    projectDocumentRevisionFiles?: {
      id: number;
      s3ContentType: string;
    }[];
  }[];
  consultant: Contractor | null;
  contractor: Contractor | null;
  subContractor: Contractor | null;
  latestRevisionId?: number;
}

export interface DocumentMasterListResponse {
  count: number;
  data: DocumentMasterListItem[];
}

export async function getDocumentMasterList({
  page,
  limit,
  sortBy,
  orderBy,
  search,
  disciplineIds,
  aimsDocumentTypeIds
}: GetDocumentMasterList): Promise<DocumentMasterListResponse> {
  const params = {
    page,
    limit,
    sortBy,
    orderBy,
    search,
    disciplineIds,
    aimsDocumentTypeIds
  };
  const { data } = await api.get('/project-documents/ios-master-lists', { params });
  return data;
}
